import React, { useEffect, useRef, useState } from "react";
import { theme } from "antd";
import useHls from "../../hooks/useHls";
import "./VideoPlayer.scss";

/**
 * Full video player with native controls for the product detail view.
 *
 * Autoplays muted by default (browsers require muted for autoplay to start),
 * shows a buffering spinner during network stalls, and falls back to the
 * poster (thumbnail) frame before playback begins.
 */
export default function VideoPlayer({
  hlsUrl,
  posterUrl,
  autoPlay = true,
  muted = true,
  ariaLabel = "",
  className = "",
}) {
  const { useToken } = theme;
  const { token } = useToken();

  const videoRef = useRef(null);
  // Don't show the spinner before any media activity — that produces a flash
  // on fast networks. Turn it on when the player starts loading or stalls,
  // off on canplay / playing.
  const [buffering, setBuffering] = useState(false);

  useHls(videoRef, hlsUrl);

  // The HTML autoplay attribute is unreliable when the source is attached
  // asynchronously by hls.js — by the time the manifest parses, the browser
  // may have given up on autoplay. Trigger play() explicitly once metadata
  // loads.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hlsUrl) return undefined;
    const tryPlay = () => {
      if (!autoPlay) return;
      const p = video.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    const onLoadStart = () => setBuffering(true);
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    const onCanPlay = () => setBuffering(false);
    video.addEventListener("loadedmetadata", tryPlay);
    video.addEventListener("loadstart", onLoadStart);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("canplay", onCanPlay);
    if (video.readyState >= 1) tryPlay();
    return () => {
      video.removeEventListener("loadedmetadata", tryPlay);
      video.removeEventListener("loadstart", onLoadStart);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("canplay", onCanPlay);
    };
  }, [hlsUrl, autoPlay]);

  if (!hlsUrl) return null;

  return (
    <div className={`videoPlayer ${className}`}>
      <video
        ref={videoRef}
        className="videoPlayer__video"
        controls
        muted={muted}
        autoPlay={autoPlay}
        playsInline
        preload="auto"
        poster={posterUrl || undefined}
        aria-label={ariaLabel}
      />
      {buffering && (
        <div className="videoPlayer__spinnerWrapper">
          <div
            className="videoPlayer__spinner"
            style={{ borderTopColor: token?.colorPrimary }}
          />
        </div>
      )}
    </div>
  );
}
